import {
    useMutation, useQuery,
} from '@tanstack/react-query';
import {
    Button, Flex, Modal, Select, Spin,
} from 'antd';
import {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import {
    projectsQueries, scriptsMutations,
} from '@/api';
import { ConfirmModal } from '@/components/ConfirmModal';

type ScriptCopyModalProps = {
    open: boolean;
    onClose: () => void;
    sourceScriptId: string;
    scriptTitle?: string | null;
};

export const ScriptCopyModal = ({
    open,
    onClose,
    sourceScriptId,
    scriptTitle,
}: ScriptCopyModalProps) => {
    const navigate = useNavigate();
    const [targetProjectId, setTargetProjectId] = useState<string | undefined>();
    const [confirmOpen, setConfirmOpen] = useState(false);

    const { data: projectsData, isLoading: isLoadingProjects } = useQuery({
        ...projectsQueries.getAllProjects({
            order_column: 'title',
            order_direction: 'asc',
            per_page: -1,
            current_page: 1,
            search: '',
        }),
        enabled: open,
    });

    const copyMutationOptions = useMemo(
        () => scriptsMutations.copy(),
        [],
    );
    const { mutate: copyScript, isPending } = useMutation(copyMutationOptions);

    useEffect(() => {
        if (!open) {
            setTargetProjectId(undefined);
            setConfirmOpen(false);
        }
    }, [open]);

    const selectedProjectTitle = useMemo(
        () => projectsData?.items?.find((p) => p.id === targetProjectId)?.title,
        [projectsData, targetProjectId],
    );

    const handleRequestCopy = useCallback(() => {
        if (targetProjectId) {
            setConfirmOpen(true);
        }
    }, [targetProjectId]);

    const handleConfirmCopy = useCallback(() => {
        if (!targetProjectId) {
            return;
        }
        copyScript(
            {
                source_script_id: sourceScriptId,
                target_project_id: targetProjectId,
            },
            {
                onSuccess: (data) => {
                    setConfirmOpen(false);
                    onClose();
                    if (data.project_id && data.id) {
                        navigate(`/projects/${data.project_id}/scripts/${data.id}`);
                    }
                },
            },
        );
    }, [copyScript, sourceScriptId, targetProjectId, onClose, navigate]);

    const handleCancelConfirm = useCallback(() => {
        setConfirmOpen(false);
    }, []);

    const handleModalCancel = useCallback(() => {
        onClose();
    }, [onClose]);

    const selectOptions = useMemo(
        () =>
            projectsData?.items?.map((project) => ({
                value: project.id,
                label: project.title,
            })) ?? [],
        [projectsData],
    );

    return (
        <>
            <Modal
                title="Копіювання скрипту"
                open={open}
                onCancel={handleModalCancel}
                footer={null}
                destroyOnHidden
            >
                {isLoadingProjects ? (
                    <Flex justify="center" align="center" style={{ minHeight: 120 }}>
                        <Spin />
                    </Flex>
                ) : (
                    <Flex vertical gap="middle">
                        <Select
                            placeholder="Оберіть проєкт"
                            style={{ width: '100%' }}
                            value={targetProjectId}
                            onChange={(value) => setTargetProjectId(value)}
                            options={selectOptions}
                            showSearch
                            optionFilterProp="label"
                        />
                        <Flex justify="flex-end" gap="small">
                            <Button onClick={handleModalCancel}>
                                Скасувати
                            </Button>
                            <Button
                                type="primary"
                                disabled={!targetProjectId}
                                onClick={handleRequestCopy}
                            >
                                Копіювати
                            </Button>
                        </Flex>
                    </Flex>
                )}
            </Modal>
            <ConfirmModal
                open={confirmOpen}
                mode="copy"
                entityType="script"
                entityName={scriptTitle}
                copyTargetName={selectedProjectTitle}
                onConfirm={handleConfirmCopy}
                onCancel={handleCancelConfirm}
                loading={isPending}
            />
        </>
    );
};
